import { RiDoubleQuotesL, RiStarFill } from 'react-icons/ri'
import { useInView } from '@/hooks'

const testimonials = [
  {
    quote:
      "Simone was so helpful throughout my pregnancy journey and postpartum. Through our conversations she was so easy to talk to and so polite. Her guidance kept me prepared for the labour room, so now she's my go to doula!",
    name: 'Shaloma',
    role: 'First-Time Mother',
  },
  {
    quote:
      "I wasn't keen on the idea of having a stranger involved in an intimate and life changing period in our family. However, my partner wanted a doula. Simone has been brilliant to have on this journey so far. I see the value in having a doula throughout the process especially as a first time father. The information, resources and emotional support Simone has provided to us is invaluable.",
    name: 'Stephen',
    role: 'First-Time Father',
  },
  {
    quote:
      "While struggling with Hyperemesis Gravidarum, I experienced one of the hardest weeks of my pregnancy, both physically and emotionally. Simone checked in on me, knowing how much HG can impact an expectant mother's mental health. Simone offered support, reassurance, and a listening ear when I needed it most. Her kindness and genuine care made a difficult time feel far less lonely, and I am incredibly grateful.",
    name: 'Linda',
    role: 'First-Time Mother',
  },
]

export const TestimonialsSection = () => {
  const { ref, inView } = useInView<HTMLDivElement>()

  return (
    <section className="relative bg-crimson-900 py-20 lg:py-28 overflow-hidden">
      {/* Decorative circles */}
      <div
        aria-hidden="true"
        className="absolute top-0 right-0 w-96 h-96 rounded-full bg-crimson-700 opacity-25 -translate-y-1/3 translate-x-1/3"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-0 w-72 h-72 rounded-full bg-crimson-700 opacity-20 translate-y-1/3 -translate-x-1/4"
      />

      <div className="relative z-10 max-w-[1200px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div ref={ref} className="text-center mb-14 space-y-4">
          <h2
            className={`font-display font-bold text-white leading-tight ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{
              fontSize: 'clamp(1.85rem, 4vw, 3rem)',
              animationDelay: inView ? '0.1s' : undefined,
            }}
          >
            Stories of Strength,{' '}
            <em className="text-gold-400 not-italic italic">Support &amp; Self-Trust</em>
          </h2>

          <div
            className={`w-12 h-[3px] rounded-full bg-gradient-to-r from-gold-400 to-crimson-500 mx-auto ${inView ? 'animate-fade-in' : 'opacity-0'}`}
            style={{ animationDelay: inView ? '0.2s' : undefined }}
          />
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div
              key={t.name}
              className={`relative flex flex-col bg-[#FDF8F0] border border-stone-200 rounded-3xl overflow-hidden transition-all duration-300 hover:shadow-[0_12px_32px_rgba(0,0,0,0.12)] hover:border-gold-400 ${inView ? 'animate-fade-in-up' : 'opacity-0'}`}
              style={{ animationDelay: inView ? `${0.1 + i * 0.12}s` : undefined }}
            >
              {/* Top accent bar */}
              <div className="h-[3px] bg-gradient-to-r from-crimson-600 via-gold-500 to-crimson-600" />

              <div className="flex flex-col gap-5 p-7 flex-1">
                {/* Quote icon + stars row */}
                <div className="flex items-center justify-between">
                  <RiDoubleQuotesL size={32} className="text-crimson-600 shrink-0" />
                  <div className="flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, s) => (
                      <RiStarFill key={s} size={13} className="text-gold-500" />
                    ))}
                  </div>
                </div>

                {/* Quote text */}
                <p className="font-body text-base-800 text-[15px] leading-[1.9] flex-1">
                  &ldquo;{t.quote}&rdquo;
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-stone-200">
                  <div className="w-10 h-10 rounded-full bg-crimson-100 flex items-center justify-center shrink-0">
                    <span className="font-display font-bold text-crimson-700 text-base leading-none">
                      {t.name[0]}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-base-900 text-[14.5px] leading-tight">
                      {t.name}
                    </p>
                    <p className="text-crimson-600 text-[11px] uppercase tracking-[0.18em] font-medium mt-0.5">
                      {t.role}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
